import { FC, FormEvent, useState } from 'react';
import { formOptions } from '@tanstack/react-form';
import { CardElement } from '@stripe/react-stripe-js';
import { useDeleteCardMutation } from '@teachur/http-client';
import { toast } from 'react-toastify';

import { useAppForm } from '@new/common/hooks/form';
import { Button } from '@new/common/ui/Button/Button';
import { BILLING_DAY_OPTIONS } from '../../constants';
import { getBillingDefaultValues, getBillingDay } from '../../lib/helpers';

interface BillingInfoFormProps {
  user: User;
  billingInfo: BillingInfo;
  onUpdateSubscriptionDay: (billingDay: number) => void;
  onAddCard: () => void;
  isAddingCard: boolean;
}

const billingFormOpts = formOptions({
  defaultValues: {
    firstName: '',
    lastName: '',
    country: '',
    city: '',
    address: '',
    state: '',
    zipCode: '',
  },
});

const CARD_ELEMENT_OPTIONS = {
  hidePostalCode: true,
  style: {
    base: {
      fontSize: '14px',
      fontFamily: 'var(--font-montserrat), sans-serif',
      color: '#1a1a1a',
      '::placeholder': { color: '#a0a0a0' },
    },
    invalid: { color: '#e53e3e' },
  },
};

export const BillingInfoForm: FC<BillingInfoFormProps> = ({
  user,
  billingInfo,
  onUpdateSubscriptionDay,
  onAddCard,
  isAddingCard,
}) => {
  const card = billingInfo?.cards?.[0];
  const currentBillingDay = getBillingDay(billingInfo);
  const hasSubscription = !!billingInfo?.customer?.subscriptions?.[0];
  const cardBrand = (card?.brand ?? card?.card?.brand ?? '').toUpperCase();
  const cardLast4 = card?.last4 ?? card?.card?.last4 ?? '';
  const cardExpMonth = card?.expMonth ?? card?.card?.exp_month;
  const cardExpYear = card?.expYear ?? card?.card?.exp_year;
  const [selectedDay, setSelectedDay] = useState<number>(currentBillingDay);
  const { mutate: deleteCard, isPending: isDeletingCard } = useDeleteCardMutation({
    onSuccess: () => toast.success('Card removed successfully.'),
    onError: () => toast.error('Failed to remove card.'),
  });

  const form = useAppForm({
    ...billingFormOpts,
    defaultValues: getBillingDefaultValues(user, billingInfo),
    validators: {
      onSubmit: ({ value }) => {
        // TODO: submit billing info update
        console.log('Submitting billing info:', value);
        return undefined;
      },
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    form.handleSubmit();
  };

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-5">
      {/* TODO: Billing address fields hidden until Stripe integration is complete
      {renderRows(BILLING_ADDRESS_ROWS)}

      <form.AppField
        name="address"
        children={(field) => (
          <field.Textarea label="Address" placeholder="Enter your address" disabled />
        )}
      />

      {renderRows(BILLING_ADDRESS_ROWS_BOTTOM)}

      <form.AppForm>
        <form.SubmitButton label="Save changes" />
      </form.AppForm>
      */}

      <div className="flex items-center justify-between mt-4">
        <h3 className="[font-family:var(--font-arvo)] [font-size:18px] font-bold text-(--neutral-black-NB1000)! m-0">
          Payment Method
        </h3>
      </div>

      {card && (
        <div className="flex items-center justify-between rounded-lg border border-solid border-(--neutral-white-NW600) p-4">
          <div className="flex flex-col gap-1">
            <span className="[font-family:var(--font-montserrat)] [font-size:14px] font-medium text-(--neutral-black-NB1000)">
              {cardBrand} •••• {cardLast4}
            </span>
            <span className="[font-family:var(--font-montserrat)] [font-size:12px] text-(--neutral-black-NB700)">
              Expires {cardExpMonth ?? '—'}/{cardExpYear ?? '—'}
            </span>
          </div>
          <button
            type="button"
            onClick={() => deleteCard(card.id)}
            disabled={isDeletingCard}
            className="bg-transparent border border-solid border-red-300 rounded-lg px-4 py-1.5 cursor-pointer text-red-600 [font-family:var(--font-montserrat)] [font-size:13px] font-medium hover:bg-red-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isDeletingCard ? 'Removing...' : 'Remove'}
          </button>
        </div>
      )}

      <div className="flex flex-col gap-3">
        <span className="h7 xl:h6 regular text-[var(--neutral-black-NB1000)]">
          {card ? 'Replace card' : 'Add a card'}
        </span>
        <div className="rounded-lg border border-solid border-(--neutral-white-NW600) p-3">
          <CardElement options={CARD_ELEMENT_OPTIONS} />
        </div>
        <Button
          type="button"
          onClick={onAddCard}
          disabled={isAddingCard}
          className="self-start rounded-lg bg-[#FE8701] px-6 py-2 border-0 cursor-pointer text-white [font-family:var(--font-montserrat)] [font-size:14px] font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isAddingCard ? 'Adding...' : 'Add card'}
        </Button>
      </div>

      {hasSubscription ? (
        <div className="rounded-lg border border-solid border-(--neutral-white-NW600) p-4 mt-2">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-2 h-2 rounded-full bg-green-500" />
            <span className="[font-family:var(--font-montserrat)] [font-size:14px] font-medium text-(--neutral-black-NB1000)">
              Active Subscription
            </span>
          </div>

          <p className="[font-family:var(--font-montserrat)] [font-size:14px] text-(--neutral-black-NB700) m-0 mb-4">
            You are charged on the <strong>{currentBillingDay}th</strong> of each month.
          </p>

          <div className="flex items-center gap-3">
            <label className="[font-family:var(--font-montserrat)] [font-size:13px] text-(--neutral-black-NB700)">
              Change billing day to:
            </label>
            <select
              value={selectedDay}
              onChange={(e) => setSelectedDay(Number(e.target.value))}
              className="appearance-none border border-solid border-(--neutral-white-NW600) rounded-lg px-3 py-1.5 pr-8 [font-family:var(--font-montserrat)] [font-size:14px] bg-white outline-none cursor-pointer bg-[url('data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23666%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[position:right_8px_center] hover:border-(--neutral-black-NB700) transition-colors"
            >
              {BILLING_DAY_OPTIONS.map((day) => (
                <option key={day} value={day}>
                  {day}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => onUpdateSubscriptionDay(selectedDay)}
              disabled={selectedDay === currentBillingDay}
              className="rounded-lg bg-[#FE8701] px-4 py-1.5 border-0 cursor-pointer text-white [font-family:var(--font-montserrat)] [font-size:13px] font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Update
            </button>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-solid border-(--neutral-white-NW600) bg-(--neutral-white-NW900) p-4 mt-2">
          <p className="[font-family:var(--font-montserrat)] [font-size:14px] text-(--neutral-black-NB700) m-0">
            No subscription found. Please choose a subscription plan to manage your billing details.
          </p>
        </div>
      )}
    </form>
  );
};
